import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { trackEvent } from '@/utils/analytics/useAnalytics'

import { Box, Divider } from '@chakra-ui/react'

import { MailIcon } from '@hivewatch/dark'

import { IncidentIcon, QuickReportIcon } from '@/components/icons'
import { ButtonTag, IncidentDescription } from '@/components/ui'
import { IncidentQuery } from '@/graphql/generated/operations'
import { IncidentSource } from '@/graphql/generated/schemas'
import { useAppDispatch } from '@/redux/hooks'
import {
  closeSOPIncidentDrawer,
  closeWorkflowDrawer,
  toggleIncidentTraceLogDrawer,
} from '@/redux/ui/uiSlice'
import { BUTTON_PRESS } from '@/utils/analytics'
import {
  getEmailIncidentSourceLabel,
  isCustomQuickReportIncident,
  isEmailSourcedIncident,
  isManualIncident,
} from '@/utils/incidents'

import { ReasoningTrailCard } from '../../ai-operator-reasoning-trail'
import { MixpanelDataIProps } from '../../shared/types/types'
import { shouldShowTraceLogs } from '../../shared/utils/feedUtils'
import {
  VIEW_TL_EVENT,
  getMixpanelEventData,
} from '../../shared/utils/traceLogUtils'
import { CustomQuickReportDetail } from './CustomQuickReportDetail'
import { FeedButtons } from './FeedButtons'
import { FeedItem } from './FeedItem'
import { IncidentHeader } from './IncidentAction'
import { QuickReportDetail } from './QuickReportDetail'

interface IncidentDetailsIProps {
  incident?: IncidentQuery['incident']
  mixpanelData?: MixpanelDataIProps
}

export const IncidentDetails = ({
  incident,
  mixpanelData,
}: IncidentDetailsIProps) => {
  const dispatch = useAppDispatch()
  const device = incident?.devices[0]
  const source = incident?.source
  const {
    salesDatabaseReset: isSalesDatabaseReset,
    traceLogs: isTraceLogsEnabled,
    useIncidentTypeDefinition: isIncidentTypeDefinition,
  } = useFeatureFlags()
  const slug = isIncidentTypeDefinition ? incident?.incidentType?.slug : null
  const isManual = isManualIncident({
    incidentType: incident?.incidentType,
    manualIncidentCategory: incident?.manualIncidentCategory,
  })
  // Email-generated incidents (Sensoscientific, Honeywell Mass Comms) get a
  // dedicated treatment: source name as the card title and the raw alert
  // email as the body. Gated behind the sales database reset flag until it
  // graduates beyond the sales environment.
  const isEmailIncident = isSalesDatabaseReset && isEmailSourcedIncident(source)
  const isQuickReportIncident =
    source === IncidentSource.QuickReport ||
    source === IncidentSource.CustomQuickReport
  const isCustomQuickReport = isCustomQuickReportIncident(incident)

  const openTraceLogDrawer = () => {
    dispatch(closeSOPIncidentDrawer())
    dispatch(closeWorkflowDrawer())
    dispatch(toggleIncidentTraceLogDrawer())
    const mixpanelData = getMixpanelEventData(incident)
    trackEvent(`${BUTTON_PRESS} ${VIEW_TL_EVENT}`, mixpanelData)
  }
  return (
    <Box>
      <FeedItem
        actionType={incident?.status}
        createdAt={incident?.createdAt}
        facilityTimeZone={incident?.facility?.timeZone}
        heading={
          <IncidentHeader
            incident={incident}
            titleOverride={
              isEmailIncident ? getEmailIncidentSourceLabel(source) : undefined
            }
          />
        }
        icon={
          source === IncidentSource.QuickReport ? (
            <QuickReportIcon />
          ) : source === IncidentSource.Sensoscientific ||
            source === IncidentSource.HoneywellMassComms ? (
            <MailIcon colorScheme='light' size={30} />
          ) : (
            <IncidentIcon
              incidentType={incident?.type}
              isManualIncident={isManual}
              name={incident?.name}
              size={30}
              slug={incident?.incidentType?.slug}
            />
          )
        }
      >
        {isEmailIncident ? (
          <Box whiteSpace='pre-line'>{incident?.description}</Box>
        ) : (
          <IncidentDescription
            creator={incident?.creator}
            description={isCustomQuickReport ? null : incident?.description}
            deviceId={device?.id}
            deviceName={device?.name}
            deviceType={device?.type}
            floorId={incident?.floor?.id}
            floorName={incident?.floor?.name}
            isManualIncident={isManual}
            manualIncidentCategoryName={incident?.manualIncidentCategory?.name}
            name={incident?.name}
            slug={slug}
            type={incident?.type}
          />
        )}
        {isTraceLogsEnabled && shouldShowTraceLogs(incident?.type, slug) && (
          <ButtonTag
            mt='10px'
            onClick={openTraceLogDrawer}
            style={{
              padding: '3px 20px',
            }}
          >
            View Trace Logs
          </ButtonTag>
        )}

        {isQuickReportIncident &&
          (isCustomQuickReport ? (
            <CustomQuickReportDetail details={incident?.quickReportDetails} />
          ) : (
            <QuickReportDetail
              detail={incident?.externalDetail}
              incidentCategory={incident?.manualIncidentCategory?.name}
            />
          ))}
      </FeedItem>

      <FeedButtons incident={incident} mixpanelData={mixpanelData} />

      <ReasoningTrailCard
        incidentId={incident?.id}
        wasProcessedByAiOperator={incident?.wasProcessedByAiOperator}
      />

      <Divider borderColor='#DFE3EF' my='20px' w='100%' />
    </Box>
  )
}
